<!--
  One half of the plan — the retain side or the acquire side. Used twice, hence the section prop.

  The summary is editable in place: the AI drafts it, the marketer owns it. Edits save the plan header only,
  so they can never clobber items or an in-flight draft.
-->
<template>
  <v-card
    outlined
    class="fill-height"
  >
    <v-card-title class="text-subtitle-1">
      {{ title }}
      <v-spacer />
      <v-btn
        v-if="!editing && section"
        icon
        small
        title="Edit"
        @click="startEdit"
      >
        <v-icon small>
          mdi-pencil
        </v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <div
        v-if="!section"
        class="text-caption grey--text"
      >
        No plan yet — generate one to fill this in.
      </div>

      <template v-else>
        <template v-if="editing">
          <v-textarea
            v-model="draftSummary"
            outlined
            dense
            auto-grow
            rows="3"
            label="Approach"
            hide-details
            class="mb-2"
          />
          <!-- Pace is edited per channel: email is capped and costs unsubscribes, social is cheap and
               compounds, and the blog is a count of commissioned posts. One box for all three could only ever
               hold whichever one the marketer thought of last. -->
          <v-text-field
            v-model="draftEmailCadence"
            outlined
            dense
            label="Email pace"
            placeholder="one broadcast every 3 weeks"
            hide-details
            class="mb-2"
          />
          <v-text-field
            v-model="draftSocialCadence"
            outlined
            dense
            label="Social pace"
            placeholder="two pins a week"
            hide-details
            class="mb-2"
          />
          <div class="d-flex mb-2">
            <v-text-field
              v-model.number="draftBlogPostCount"
              outlined
              dense
              type="number"
              min="0"
              label="Blog posts"
              hide-details
              class="mr-2 blog-count"
            />
            <v-text-field
              v-model="draftBlogCadence"
              outlined
              dense
              label="Blog pace"
              placeholder="one now, the second once it ranks"
              hide-details
            />
          </div>
          <v-textarea
            v-if="draftCadence"
            v-model="draftCadence"
            outlined
            dense
            auto-grow
            rows="2"
            label="Cadence (legacy)"
            hint="Drafted before the pace was split per channel. Clear it once the fields above say the same thing."
            persistent-hint
            class="mb-2"
          />
          <v-btn
            small
            color="primary"
            :loading="saving"
            @click="save"
          >
            Save
          </v-btn>
          <v-btn
            small
            text
            :disabled="saving"
            @click="editing = false"
          >
            Cancel
          </v-btn>
        </template>

        <template v-else>
          <p class="text-body-2">
            {{ section.summary }}
          </p>

          <div
            v-if="paceRows.length"
            class="mb-3"
          >
            <div
              v-for="row in paceRows"
              :key="row.icon"
              class="d-flex align-start"
            >
              <v-icon
                small
                class="mr-1 mt-1"
              >
                {{ row.icon }}
              </v-icon>
              <span class="text-caption">{{ row.text }}</span>
            </div>
          </div>

          <div
            v-if="(section.themes || []).length"
            class="mb-3"
          >
            <div class="text-caption grey--text mb-1">
              Themes
            </div>
            <v-chip
              v-for="theme in section.themes"
              :key="theme"
              x-small
              class="mr-1 mb-1"
            >
              {{ theme }}
            </v-chip>
          </div>

          <template v-if="(section.sequence || []).length">
            <v-divider class="my-3" />
            <div class="text-caption grey--text mb-2">
              Sequence
            </div>
            <!--
              A step with items is a link to them: the cadence describes the touch, the Suggestions tab is
              where it gets worked, and having to go and find the matching card by eye was the missing half.
            -->
            <div
              v-for="(step, index) in section.sequence"
              :key="step.key || step.order || index"
              class="d-flex align-start mb-2 step"
              :class="{ 'step--linked': stepItems(step).length }"
              :title="stepLinkTitle(step)"
              @click="openStep(step)"
            >
              <v-chip
                x-small
                outlined
                class="mr-2 mt-1 flex-shrink-0"
              >
                {{ dayLabel(step.dayOffset) }}
              </v-chip>
              <div class="flex-grow-1">
                <div class="text-body-2">
                  {{ step.title }}
                  <!-- Kept through a redraft because it was already worked — otherwise its absence would read
                       as "the new plan dropped this", when in fact it's the one part that's settled. -->
                  <v-icon
                    v-if="step.carried"
                    x-small
                    class="ml-1 mb-1"
                    color="grey"
                    title="Kept from the previous plan — this touch was already committed to"
                  >
                    mdi-pin-outline
                  </v-icon>
                </div>
                <div class="text-caption grey--text">
                  {{ step.purpose }}<span v-if="step.ctaHint"> · {{ step.ctaHint }}</span>
                </div>
              </div>
              <!-- Where this touch actually got to. Absent when we can't tell which item is this step. -->
              <v-chip
                v-if="stepStatus(step)"
                x-small
                :color="stepStatus(step).color"
                :outlined="stepStatus(step).outlined"
                :dark="!stepStatus(step).outlined"
                class="ml-2 mt-1 flex-shrink-0"
              >
                {{ stepStatus(step).label }}
              </v-chip>
            </div>
          </template>
        </template>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { planItemStatus } from '@/helpers/marketing';

// How far a step has got, for picking one chip when several items carry the same touch. The LEAST-progressed
// wins: a step is only "Done" when nothing is left on it. Failed sits at the bottom on purpose — a send that
// blew up is the outstanding work on that touch, so it must win the chip over anything still merely planned.
const PROGRESS = { Failed: 0, Suggested: 1, Approved: 2, 'Under Review': 3, Sending: 4, Done: 5 };

// Loose title comparison — the last resort in linkMode, for plans drafted before items carried any link at
// all. Exact-after-normalising on purpose: a near-miss would hang the wrong status on a touch, which is
// worse than no chip.
function normalize(title) {
  return (title || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export default {
  name: 'AudienceStrategyPanel',
  props: {
    title: {
      type: String,
      default: ''
    },
    // An AudienceSectionPlan, or null when the plan hasn't been drafted.
    section: {
      type: Object,
      default: null
    },
    plan: {
      type: Object,
      default: () => ({})
    },
    // Which half of the plan this is: 'existingCustomerPlan' or 'newCustomerPlan'.
    field: {
      type: String,
      required: true
    },
    // Every item on the plan — this panel picks out the ones belonging to its own section.
    items: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      editing: false,
      saving: false,
      draftSummary: '',
      draftCadence: '',
      draftEmailCadence: '',
      draftSocialCadence: '',
      draftBlogPostCount: 0,
      draftBlogCadence: ''
    };
  },
  computed: {
    sectionId() {
      return this.field === 'newCustomerPlan'
        ? this.$enums.AUDIENCE.SECTION.NEW
        : this.$enums.AUDIENCE.SECTION.EXISTING;
    },
    sectionItems() {
      return this.items.filter((i) => i.section === this.sectionId);
    },
    // Which link this section's items actually carry, in descending order of how much it can be trusted:
    // the stable step key, then the display order (plans drafted before keys existed), then nothing, in
    // which case titles are all we have. Decided ONCE for the section rather than per step, so a sequence
    // can never end up half-keyed and half-guessed.
    linkMode() {
      if (this.sectionItems.some((i) => (i.detail || {}).stepKey)) return 'key';
      if (this.sectionItems.some((i) => (i.detail || {}).stepOrder > 0)) return 'order';
      return 'title';
    },
    // Whether this plan's items were drafted with the sequence link at all. When they were, a step with no
    // item is real information ("nothing was proposed for this touch") and gets said out loud; when they
    // weren't, every step would claim that, so unmatched steps stay silent.
    hasStepLinks() {
      return this.linkMode !== 'title';
    },
    // One line per channel that has a pace. `cadence` is the pre-split field and is shown last as its own
    // row, so a plan drafted before the split still says something instead of going blank.
    paceRows() {
      const s = this.section || {};
      const rows = [];

      if (s.emailCadence) rows.push({ icon: 'mdi-email-outline', text: s.emailCadence });
      if (s.socialCadence) rows.push({ icon: 'mdi-share-variant', text: s.socialCadence });

      if (s.blogPostCount > 0) {
        const posts = `${s.blogPostCount} blog post${s.blogPostCount === 1 ? '' : 's'}`;
        rows.push({
          icon: 'mdi-post-outline',
          text: s.blogCadence ? `${posts} — ${s.blogCadence}` : posts
        });
      }

      if (s.cadence) rows.push({ icon: 'mdi-calendar-clock', text: s.cadence });
      return rows;
    }
  },
  methods: {
    startEdit() {
      this.draftSummary = this.section.summary || '';
      this.draftCadence = this.section.cadence || '';
      this.draftEmailCadence = this.section.emailCadence || '';
      this.draftSocialCadence = this.section.socialCadence || '';
      this.draftBlogPostCount = this.section.blogPostCount || 0;
      this.draftBlogCadence = this.section.blogCadence || '';
      this.editing = true;
    },
    dayLabel(offset) {
      if (!offset) return 'Day 0';
      return `Day ${offset}`;
    },
    // The items that carry out this touch, resolved through whichever link this section actually has.
    stepItems(step) {
      if (this.linkMode === 'key') {
        return step.key ? this.sectionItems.filter((i) => (i.detail || {}).stepKey === step.key) : [];
      }
      if (this.linkMode === 'order') {
        return step.order ? this.sectionItems.filter((i) => (i.detail || {}).stepOrder === step.order) : [];
      }

      const title = normalize(step.title);
      return title ? this.sectionItems.filter((i) => normalize(i.title) === title) : [];
    },
    // The item a click on this touch should land on: the least-progressed one, matching what the chip
    // reports, because that is the work the marketer still has a decision to make about.
    focusItem(step) {
      const items = this.stepItems(step);
      if (!items.length) return null;

      const dismissedId = this.$enums.AUDIENCE.ITEM_STATUS_IDS.DISMISSED;
      const live = items.filter((i) => i.status !== dismissedId);
      return (live.length ? live : items).reduce((a, b) =>
        (PROGRESS[planItemStatus(b).label] < PROGRESS[planItemStatus(a).label] ? b : a));
    },
    // One chip for the touch: the least-progressed of its items, because that is the work still outstanding.
    // All-dismissed reads as Dismissed rather than as untouched — somebody decided against this touch.
    stepStatus(step) {
      const items = this.stepItems(step);
      if (!items.length) {
        return this.hasStepLinks
          ? { label: 'Not planned', color: 'grey', outlined: true }
          : null;
      }

      const dismissedId = this.$enums.AUDIENCE.ITEM_STATUS_IDS.DISMISSED;
      const live = items.filter((i) => i.status !== dismissedId);
      const chips = (live.length ? live : items).map((i) => planItemStatus(i));
      const worst = chips.reduce((a, b) => (PROGRESS[b.label] < PROGRESS[a.label] ? b : a));
      const atWorst = chips.filter((c) => c.label === worst.label).length;

      // When the items DISAGREE, the count has to be of the ones still at the least-progressed state, not of
      // the touch as a whole: "Suggested · 4" on a touch where three of the four are already approved reads
      // as "nothing here was actioned", which is exactly backwards. "Suggested 1/4" says what's outstanding.
      return {
        label: atWorst < chips.length
          ? `${worst.label} ${atWorst}/${chips.length}`
          : (chips.length > 1 ? `${worst.label} · ${chips.length}` : worst.label),
        color: worst.color,
        outlined: false
      };
    },
    // The row's tooltip does double duty: which items carry this touch, and that the row is clickable.
    stepLinkTitle(step) {
      const items = this.stepItems(step);
      if (!items.length) {
        return this.hasStepLinks ? 'No plan item covers this touch yet' : '';
      }

      // Each item's own status, not just its title: the chip reports the least-progressed one, and this is
      // where the marketer finds out WHICH item that is without going to the Suggestions tab to look.
      const titles = items.map((i) => `${i.title} — ${planItemStatus(i).label}`).join('\n');
      return `${titles}\n\nClick to open in Suggestions`;
    },
    // Hands the id up to the audience screen, which switches tabs and scrolls the card into view — the plan
    // and the suggestions are two views of the same items, so this is navigation, not a new fetch.
    openStep(step) {
      const item = this.focusItem(step);
      if (item) this.$emit('focus-item', item.id);
    },
    // Sends the whole plan header back with this section patched — the save endpoint only touches the
    // header, so items and any in-flight draft are untouched.
    save() {
      this.saving = true;
      const updated = {
        ...this.plan,
        [this.field]: {
          ...this.section,
          summary: this.draftSummary,
          cadence: this.draftCadence,
          emailCadence: this.draftEmailCadence,
          socialCadence: this.draftSocialCadence,
          // Coerced here rather than trusted from the field: v-model.number leaves '' when the box is
          // emptied, and an empty string would post through as a null count.
          blogPostCount: Math.max(0, parseInt(this.draftBlogPostCount, 10) || 0),
          blogCadence: this.draftBlogCadence
        }
      };

      this.$store
        .dispatch('audiences/savePlan', updated)
        .then(() => {
          this.editing = false;
          this.$root.$snackbarSuccess('Saved');
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style scoped>
/* Narrow enough that the blog pace beside it gets the room — the count is never more than one digit. */
.blog-count {
  max-width: 110px;
}

/* Only a touch that HAS items is a link — an unplanned one has nowhere to go, so it must not invite a click. */
.step {
  border-radius: 4px;
  padding: 2px 4px;
  margin-left: -4px;
  margin-right: -4px;
}

.step--linked {
  cursor: pointer;
}

.step--linked:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
